import React, { useEffect, useRef } from "react";
import * as bootstrap from "bootstrap";

/**
 * Shared ellipsis row-actions menu for data tables.
 * Styling comes from `_table-actions-menu.scss` (also applied to
 * existing `table td .dropdown` / `.dropup` markup).
 *
 * On open, all other `.table-actions-menu` Bootstrap dropdowns are closed
 * so only one row menu can be visible at a time (parents that call
 * stopPropagation otherwise leave previous menus stuck open).
 */
export default function TableActionsMenu({
  children,
  dropup = false,
  menuEnd = false,
  menuStyle,
  autoClose = true,
  ariaLabel = "Row actions",
  disabled = false,
  /** Escape overflow:hidden / sticky parents (e.g. table-responsive). */
  fixedPopper = true,
}) {
  const toggleRef = useRef(null);

  useEffect(() => {
    const toggle = toggleRef.current;
    if (!toggle || disabled) return undefined;

    const popperConfig = fixedPopper ? { strategy: "fixed" } : undefined;
    bootstrap.Dropdown.getOrCreateInstance(toggle, {
      autoClose: autoClose ? true : false,
      popperConfig,
    });

    const closeOtherMenus = () => {
      document
        .querySelectorAll(".table-actions-menu [data-bs-toggle='dropdown']")
        .forEach((otherToggle) => {
          if (otherToggle === toggle) return;
          const other = bootstrap.Dropdown.getInstance(otherToggle);
          if (other) {
            other.hide();
          } else {
            otherToggle.setAttribute("aria-expanded", "false");
            otherToggle.classList.remove("show");
            const menu = otherToggle
              .closest(".table-actions-menu")
              ?.querySelector(".dropdown-menu");
            menu?.classList.remove("show");
          }
        });
    };

    const onShow = () => {
      closeOtherMenus();
    };

    toggle.addEventListener("show.bs.dropdown", onShow);
    return () => {
      toggle.removeEventListener("show.bs.dropdown", onShow);
    };
  }, [autoClose, disabled, fixedPopper]);

  const wrapperClass = dropup
    ? "dropup table-actions-menu"
    : "dropdown table-actions-menu";
  const menuClass = menuEnd
    ? "dropdown-menu dropdown-menu-end"
    : "dropdown-menu";

  return (
    <div className={wrapperClass}>
      <button
        ref={toggleRef}
        className="btn btn-sm no-caret"
        type="button"
        data-bs-toggle="dropdown"
        data-bs-auto-close={autoClose ? "true" : "false"}
        aria-label={ariaLabel}
        aria-expanded="false"
        disabled={disabled}
      >
        <i className="fa fa-ellipsis-h" aria-hidden="true" />
      </button>
      <div className={menuClass} style={menuStyle}>
        {children}
      </div>
    </div>
  );
}

/**
 * Optional action row with leading icon.
 * Use inside TableActionsMenu for Edit / Delete / custom actions.
 */
export function TableActionsItem({
  children,
  icon,
  to,
  onClick,
  danger = false,
  as: Component,
  type = "button",
  className = "",
  ...rest
}) {
  const classes = [
    "dropdown-item",
    danger ? "text-danger" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {icon ? <i className={`${icon} fa-fw`} aria-hidden="true" /> : null}
      <span>{children}</span>
    </>
  );

  if (to) {
    const LinkComponent = Component;
    if (!LinkComponent) {
      throw new Error("TableActionsItem with `to` requires `as={Link}` (react-router).");
    }
    return (
      <LinkComponent to={to} className={classes} {...rest}>
        {content}
      </LinkComponent>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} {...rest}>
      {content}
    </button>
  );
}
